import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import LazyLoad from 'react-lazyload';
import { AppNoInfo, AppRemixIcon } from '@/layout/AppComponents';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import errorHandler from '@/layout/components/error/ErrorHandler';
import { axiosPath, path } from '../../../routing/helpers/router';
import * as currentPageAction from '../../../store/actions/currentPageAction';
import Translator from '../../../layout/components/helpers/Translator';
import TotalEnergiesRiskPaginator from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/TotalEnergiesRiskPaginator';
import TotalEnergiesRiskSidebar from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/Sidebar/TotalEnergiesRiskSidebar';
import ElementContainer from '../../../layout/screenSpecifics/Modules/TotalEnergiesRisk/ElementContainer';
import AppLink from '../../../routing/helpers/AppLink';
import * as moduleAction from "../../../store/actions/moduleAction";

export default function ModuleTotalEnergiesRiskActionPlanListScreen() {
  const { moduleBridgeHex, filtersHex, page } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const moduleData = useSelector(
    (s) => s?.module?.[moduleBridgeHex]?.moduleData,
  );
  const userBridge = useSelector(
    (state) => state?.module?.[moduleBridgeHex]?.userBridge,
  );
  const actionPlansPage = useSelector(
    (state) => state.currentPage?.actionPlansPage,
  );
  const actionPlanLevel1 = useSelector(
    (state) => state.currentPage?.elementLevels?.level1,
  );
  const [loading, setLoading] = useState(true);
  const [actionPlans, setActionPlans] = useState(null);

  useEffect(() => {
    return () => {
      dispatch(currentPageAction.empty());
    };
  }, []);

  // > LOAD ACTION PLANS
  const loadActionPlans = useCallback(
    async () =>
      axios
        .post(
          axiosPath('api_r_user_module_totalEnergiesRisk_actionPlan_list'),
          {
            moduleBridgeHex,
            filtersHex,
          },
        )
        .then(({ data }) => {
          if (isMounted.current) {
            setActionPlans(data);
            dispatch(currentPageAction.change('pagination', data.pagination));
            dispatch(
              currentPageAction.change(
                'actionPlansPage',
                data.elementIDs,
                true,
              ),
            );

            if (data?.actionPlanExpandedID && data?.reviewID) {
              dispatch(
                currentPageAction.change(
                  'actionPlanExpandedID',
                  data.actionPlanExpandedID,
                ),
              );
              dispatch(currentPageAction.change('reviewID', data.reviewID));
            }
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, filtersHex, page],
  );

  useEffect(() => {
    setLoading(true);
    loadActionPlans();
  }, [filtersHex]);
  // < LOAD ACTION PLANS

  // > LOAD ACTION PLAN LEVELS
  const loadActionPlanLevel = async (level) =>
    axios.post(
      axiosPath('api_r_user_module_totalEnergiesRisk_actionPlan_level'),
      {
        moduleBridgeHex,
        elementIDs: actionPlans?.elementIDs?.[page - 1],
        level,
      },
    );

  // Level 1
  useEffect(() => {
    if (actionPlans?.elementIDs) {
      setLoading(false);
      window.scrollTo(0, 0);
      if (actionPlans?.elementIDs.length > 0) {
        loadActionPlanLevel(1).then((actionPlanLvl1) => {
          dispatch(
            currentPageAction.change(
              'elementLevels',
              {
                level1: !actionPlanLevel1
                  ? actionPlanLvl1?.data
                  : { ...actionPlanLevel1, ...actionPlanLvl1?.data },
              },
              true,
            ),
          );
        });
      }
    }
  }, [actionPlans, page]);
  // < LOAD ACTION PLAN LEVELS

    // > LOAD MODULE DATA //
    const loadModuleData = useCallback(async () => {
      dispatch(
        moduleAction.fetch(
          axiosPath("api_r_user_module_totalEnergiesRisk_getModuleData"),
          "moduleData",
          {
            moduleBridgeHex,
          }
        )
      );
    }, []);
  
    useEffect(() => {
      loadModuleData();
    }, []);
    // > LOAD MODULE DATA //
  
    // < STORE CALLS
    const loadModulePermissions = useCallback(async () => {
      try {
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
            "permissions",
            {
              moduleBridgeHex,
              subType: "permissions",
            }
          )
        );
        await dispatch(
          moduleAction.fetch(
            axiosPath("api_r_user_module_totalEnergiesRisk_getPermissions"),
            "userBridge",
            {
              moduleBridgeHex,
              subType: "userBridge",
            }
          )
        );
      } catch (err) {}
    }, [moduleBridgeHex]);
  
    useEffect(() => {
      loadModulePermissions();
    }, [moduleBridgeHex]);

  if (!moduleData) return <AppNoInfo />

  return (
    <>
      <TotalEnergiesRiskSidebar activeRoute="user_module_totalEnergiesRisk_actionPlan_list" />
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: moduleData?.moduleName,
                  route: {
                    name: 'user_module_totalEnergiesRisk_risk_list',
                    params: {
                      moduleBridgeHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                { name: 'Planes de acción' },
              ]
            : [{ name: '...' }]
        }
      />
      <main role="main" className="col-md-12 col-lg-12 px-md-4 mb-5">
        <div className="justify-content-between flex-wrap flex-md-nowrap align-items-center pt-0 pb-0 mb-0">
          {userBridge?.permissions?.level === 0 && (
            <div className="mb-2">
              <AppLink
                path="user_module_totalEnergiesRisk_actionPlan_add"
                params={{
                  moduleBridgeHex,
                }}
                className="btn btn-xs btn-outline-light d-inline-flex mr-3"
              >
                Nuevo Plan de acción
              </AppLink>
            </div>
          )}
          {filtersHex !== '0' && (
            <div className="alert alert-info pointer">
              <span
                onClick={() =>
                  navigate(
                    path({
                      name: 'user_module_totalEnergiesRisk_actionPlan_list',
                      params: {
                        moduleBridgeHex,
                        page: 1,
                        filtersHex: '0',
                      },
                    }),
                  )
                }
              >
                <AppRemixIcon icon="delete-bin-6" className="mr-2" />
                <Translator file="filter" name="filter.delete" />
              </span>
              {/* <span
                className="ml-4"
                onClick={() =>
                  dispatch(
                    currentPageAction.change('auxSidebar', {
                      opened: 'filters',
                    })
                  )
                }
              >
                <AppRemixIcon icon="equalizer" className="mr-2" />
                <Translator file="module-legalCompliance" name="show.filters" />
              </span> */}
            </div>
          )}
          {loading ? (
            <AppNoInfo minHeight={window.innerHeight - 60} />
          ) : actionPlansPage && actionPlansPage.length > 0 ? (
            actionPlansPage?.[page - 1].map((rID, i) => (
              <LazyLoad key={i} height={113}>
                <ElementContainer elementID={rID} view="actionPlan" />
              </LazyLoad>
            ))
          ) : (
            <div className="alert alert-info">
              Aún no existe ningún Plan de acción
            </div>
          )}
        </div>
        {actionPlansPage?.length > 0 && actionPlansPage && (
          <TotalEnergiesRiskPaginator
            pagination={actionPlans?.pagination}
            route="user_module_totalEnergiesRisk_actionPlan_list"
            page={page}
            plantObligationPages={actionPlansPage}
            itemsPerPage={20}
            className="mt-3"
            params={{
              moduleBridgeHex,
              page,
              filtersHex,
            }}
          />
        )}
      </main>
    </>
  );
}
